import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// This page is GLOBAL (not project-scoped): available to Platform Admin
// AND anyone explicitly granted the Currency.Manage permission (via
// role_permissions, managed from the existing PermissionsPage.jsx -- not
// automatically granted to Company Operator or any other role). Neither
// AuthContext.jsx's `can` object (backed by the legacy user_profiles.role
// enum) nor useProjectAuth() (requires a selectedProject) can express
// this authorization -- both are the wrong tool for a Global page. This
// page checks is_platform_admin() OR has_global_permission('Currency.Manage')
// directly via RPC, exactly mirroring the RLS policies on
// currencies/exchange_rates. This mirror is a UX convenience only, per
// the same convention useProjectAuth.js documents for itself -- RLS
// remains the authoritative boundary. is_company_operator() is no longer
// used here (see technical-debt note in project memory: it remains
// defined but unused by this page as of the Currency.Manage migration).
function useFxAdminAccess() {
  const [allowed, setAllowed] = useState(null) // null = loading
  useEffect(() => {
    let cancelled = false
    Promise.all([
      supabase.rpc('is_platform_admin'),
      supabase.rpc('has_global_permission', { p_permission: 'Currency.Manage' }),
    ]).then(([a, b]) => {
      if (cancelled) return
      setAllowed(!!a.data || !!b.data)
    })
    return () => { cancelled = true }
  }, [])
  return allowed
}

const SOURCE_OPTIONS = ['manual', 'central_bank', 'approved_provider']

export default function CurrencyExchangeRatesPage() {
  const allowed = useFxAdminAccess()
  const [tab, setTab] = useState('currencies') // 'currencies' | 'rates'
  const [currencies, setCurrencies] = useState([])
  const [rates, setRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [showAddRate, setShowAddRate] = useState(false)
  const [rateForm, setRateForm] = useState({ from_currency: '', to_currency: '', rate: '', effective_date: new Date().toISOString().split('T')[0], source: 'manual' })
  const [savingRate, setSavingRate] = useState(false)
  const [sameDayWarning, setSameDayWarning] = useState(false)

  useEffect(() => { if (allowed) loadAll() }, [allowed])

  async function loadAll() {
    setLoading(true)
    const [{ data: c }, { data: r }] = await Promise.all([
      supabase.from('currencies').select('*').order('code'),
      supabase.from('exchange_rates').select('*').order('effective_date', { ascending: false }).order('created_at', { ascending: false }),
    ])
    setCurrencies(c || [])
    setRates(r || [])
    setLoading(false)
  }

  async function toggleCurrency(code, currentlyActive) {
    setMsg(null)
    const { error } = await supabase.from('currencies').update({ is_active: !currentlyActive }).eq('code', code)
    if (error) {
      // The deactivation guard trigger raises a plain-text exception when
      // the currency is in use -- surfaced verbatim, it already explains
      // the situation clearly (no need to re-word it here).
      setMsg({ type: 'error', text: error.message })
      return
    }
    setMsg({ type: 'success', text: `${code} ${!currentlyActive ? 'activated' : 'deactivated'}.` })
    loadAll()
  }

  // Warn (non-blocking) when a rate already exists for the same pair/date
  // -- this does not invent a new financial rule; get_exchange_rate()'s
  // existing "latest created_at wins" behavior is unchanged either way.
  useEffect(() => {
    if (!rateForm.from_currency || !rateForm.to_currency || !rateForm.effective_date) { setSameDayWarning(false); return }
    const exists = rates.some(r =>
      r.from_currency === rateForm.from_currency &&
      r.to_currency === rateForm.to_currency &&
      r.effective_date === rateForm.effective_date
    )
    setSameDayWarning(exists)
  }, [rateForm.from_currency, rateForm.to_currency, rateForm.effective_date, rates])

  async function saveRate(e) {
    e.preventDefault()
    if (rateForm.from_currency === rateForm.to_currency) {
      setMsg({ type: 'error', text: 'From and To currency must be different.' })
      return
    }
    const rateNum = parseFloat(rateForm.rate)
    if (!(rateNum > 0)) {
      setMsg({ type: 'error', text: 'Rate must be greater than 0.' })
      return
    }
    setSavingRate(true)
    const { data: { user } } = await supabase.auth.getUser()
    const { error } = await supabase.from('exchange_rates').insert({
      from_currency: rateForm.from_currency,
      to_currency: rateForm.to_currency,
      rate: rateNum,
      effective_date: rateForm.effective_date,
      source: rateForm.source,
      created_by: user?.id || null,
    })
    setSavingRate(false)
    if (error) { setMsg({ type: 'error', text: 'Failed: ' + error.message }); return }
    setMsg({ type: 'success', text: 'Exchange rate added.' })
    setShowAddRate(false)
    setRateForm({ from_currency: '', to_currency: '', rate: '', effective_date: new Date().toISOString().split('T')[0], source: 'manual' })
    loadAll()
  }

  if (allowed === null) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#9A948C', fontSize: 13 }}>Loading…</div>
  )
  if (!allowed) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#9A948C', fontSize: 13 }}>Access denied — you don't have permission to view this page.</div>
  )

  const activeCurrencies = currencies.filter(c => c.is_active)

  return (
    <div className="min-h-screen font-sans" style={{ background: '#F5F1EA' }}>
      <div style={{ background: '#F5F1EA', borderBottom: '1px solid #DDD9D0', padding: '20px 32px' }}>
        <div className="max-w-4xl mx-auto">
          <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.14em', color: '#9A948C', marginBottom: 4 }}>Setup</p>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C1A18' }}>Currency & Exchange Rates</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto" style={{ padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {msg && (
          <div style={{ padding: '10px 16px', borderRadius: 8, background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`, fontSize: 12, color: msg.type === 'success' ? '#15803d' : '#991b1b', display: 'flex', justifyContent: 'space-between' }}>
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #DDD9D0' }}>
          {[{ id: 'currencies', label: 'Currencies' }, { id: 'rates', label: 'Exchange Rates' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '10px 16px', fontSize: 12, fontWeight: 700, background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid #1A7A4A' : '2px solid transparent', color: tab === t.id ? '#1A7A4A' : '#9A948C', cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9A948C', fontSize: 13 }}>Loading…</div>
        ) : tab === 'currencies' ? (
          <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F5F1EA', borderBottom: '1px solid #DDD9D0' }}>
                  {['Code', 'Name', 'Symbol', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#9A948C' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currencies.map(c => (
                  <tr key={c.code} style={{ borderBottom: '1px solid #F5F1EA' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1C1A18' }}>{c.code}</td>
                    <td style={{ padding: '10px 14px', color: '#6B655C' }}>{c.name}</td>
                    <td style={{ padding: '10px 14px', color: '#6B655C' }}>{c.symbol || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: c.is_active ? '#f0fdf4' : '#f3f4f6', color: c.is_active ? '#15803d' : '#6b7280' }}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button onClick={() => toggleCurrency(c.code, c.is_active)}
                        style={{ fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 6, border: '1px solid #DDD9D0', background: '#fff', color: c.is_active ? '#A32D2D' : '#1A7A4A', cursor: 'pointer' }}>
                        {c.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddRate(true)}
                style={{ fontSize: 13, fontWeight: 700, padding: '10px 18px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                + Add Exchange Rate
              </button>
            </div>

            <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#F5F1EA', borderBottom: '1px solid #DDD9D0' }}>
                    {['From', 'To', 'Rate', 'Effective Date', 'Source', 'Created At'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#9A948C' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rates.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9A948C' }}>No exchange rates yet.</td></tr>
                  ) : rates.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #F5F1EA' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1C1A18' }}>{r.from_currency}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1C1A18' }}>{r.to_currency}</td>
                      <td style={{ padding: '10px 14px', color: '#1C1A18' }}>{r.rate}</td>
                      <td style={{ padding: '10px 14px', color: '#6B655C' }}>{r.effective_date}</td>
                      <td style={{ padding: '10px 14px', color: '#6B655C' }}>{r.source}</td>
                      <td style={{ padding: '10px 14px', color: '#9A948C', fontSize: 11 }}>{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Exchange Rate modal -- create-only. No edit/delete anywhere:
          historical rates are immutable at the database level
          (trg_exchange_rate_immutability), and this UI does not attempt
          to fake that capability client-side. */}
      {showAddRate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowAddRate(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15, color: '#1C1A18' }}>Add Exchange Rate</h3>
            <form onSubmit={saveRate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9A948C', marginBottom: 4 }}>From Currency</p>
                  <select required value={rateForm.from_currency} onChange={e => setRateForm(f => ({ ...f, from_currency: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                    <option value="">— Select —</option>
                    {activeCurrencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                </div>
                <div>
                  <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9A948C', marginBottom: 4 }}>To Currency</p>
                  <select required value={rateForm.to_currency} onChange={e => setRateForm(f => ({ ...f, to_currency: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                    <option value="">— Select —</option>
                    {activeCurrencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9A948C', marginBottom: 4 }}>
                  Rate <span style={{ color: '#9A948C', fontWeight: 400, textTransform: 'none' }}>(1 From = X To)</span>
                </p>
                <input required type="number" step="0.0001" min="0.0001" value={rateForm.rate}
                  onChange={e => setRateForm(f => ({ ...f, rate: e.target.value }))}
                  placeholder="e.g. 3.75"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12 }} />
              </div>
              <div>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9A948C', marginBottom: 4 }}>Effective Date</p>
                <input required type="date" value={rateForm.effective_date}
                  onChange={e => setRateForm(f => ({ ...f, effective_date: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12 }} />
              </div>
              <div>
                <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9A948C', marginBottom: 4 }}>Source</p>
                <select value={rateForm.source} onChange={e => setRateForm(f => ({ ...f, source: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                  {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {sameDayWarning && (
                <div style={{ fontSize: 11, color: '#B06D00', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 10px' }}>
                  ⚠ A rate for this pair and date already exists. The most recently added one will be used going forward — this does not overwrite the existing record.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" onClick={() => setShowAddRate(false)}
                  style={{ flex: 1, padding: '10px', border: '1px solid #DDD9D0', borderRadius: 6, background: '#fff', color: '#6B655C', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={savingRate}
                  style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 6, background: '#1A7A4A', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {savingRate ? 'Saving…' : 'Add Rate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
